
import { config } from '@/config/env';

// Security utilities for input validation and sanitization

/**
 * Validates JWT token format (basic client-side validation)
 */
export const validateJWTFormat = (token: string): boolean => {
  if (!token || typeof token !== 'string') return false;
  
  // JWT should have 3 parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3) return false;
  
  // Each part should be base64url encoded
  const base64urlRegex = /^[A-Za-z0-9_-]+$/;
  return parts.every(part => part.length > 0 && base64urlRegex.test(part));
};

/**
 * Sanitizes request payload to prevent injection attacks
 */
export const sanitizeRequestPayload = (payload: any): any => {
  if (typeof payload !== 'object' || payload === null) {
    return payload;
  }

  if (Array.isArray(payload)) {
    return payload.map(sanitizeRequestPayload);
  }

  const sanitized: any = {};
  for (const [key, value] of Object.entries(payload)) {
    // Sanitize string values
    if (typeof value === 'string') {
      // Remove potentially dangerous characters
      sanitized[key] = value
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove script tags
        .replace(/javascript:/gi, '') // Remove javascript: URLs
        .replace(/on\w+\s*=/gi, '') // Remove event handlers
        .trim();
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeRequestPayload(value);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
};

/**
 * Generates a secure random nonce for CSRF protection
 */
export const generateSecureNonce = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Validates timestamp for CSRF protection
 */
export const validateTimestamp = (timestamp: number, nonce?: string): boolean => {
  if (!timestamp || typeof timestamp !== 'number') return false;
  
  const now = Date.now();
  const age = Math.abs(now - timestamp);
  
  // Check if timestamp is within acceptable range
  if (age > config.security.maxTimestampAge) return false;
  
  // Additional validation with nonce if provided
  if (nonce && typeof nonce === 'string') {
    // Basic nonce validation (should be alphanumeric and sufficient length)
    const nonceRegex = /^[a-fA-F0-9]{32,}$/;
    return nonceRegex.test(nonce);
  }
  
  return true;
};

/**
 * Creates a secure request payload with CSRF protection
 */
export const createSecurePayload = (data: any): any => {
  const nonce = generateSecureNonce();
  const timestamp = Date.now();
  
  return sanitizeRequestPayload({
    ...data,
    timestamp,
    nonce,
    _csrf: nonce, // Double-submit cookie pattern
  });
};

/**
 * Sanitizes error messages to prevent information disclosure
 */
export const sanitizeErrorMessage = (error: any, isProduction: boolean = config.app.isProd): string => {
  if (!isProduction) {
    // In development, show detailed errors
    if (error instanceof Error) return error.message;
    if (typeof error === 'string') return error;
    return 'An error occurred';
  }

  // In production, return generic messages for security
  if (error && typeof error === 'object') {
    // Map specific error types to user-friendly messages
    if ('statusCode' in error) {
      switch (error.statusCode) {
        case 401:
          return 'Authentication required. Please sign in.';
        case 403:
          return 'Access denied. You do not have permission to perform this action.';
        case 404:
          return 'The requested resource was not found.';
        case 429:
          return 'Too many requests. Please try again later.';
        case 500:
          return 'A server error occurred. Please try again.';
        default:
          return 'An error occurred. Please try again.';
      }
    }
  }

  return 'An error occurred. Please try again.';
};

/**
 * Logs security events (in a real app, this would integrate with monitoring)
 */
export const logSecurityEvent = (event: string, details: any = {}) => {
  const logData = {
    event,
    timestamp: new Date().toISOString(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...details,
  };

  if (config.app.isDev) {
    console.warn('🔒 Security Event:', logData);
  }

  // In production, this would send to monitoring service
  // e.g., analytics service, logging service, etc.
};
